import React from 'react';
import { useComparisonContext } from '../hooks/useComparisonContext';
import { VersionLogKeyValueTableRecord } from '../../version-log/records/VersionLogKeyValueTableRecord';
import { VersionLogRow } from '../../version-log/records/VersionLogRow';
import { resolveRow, ResolvedRowValue } from '../../version-log/valueFormat';
import { getCellPresentation } from '../diffHighlight';
import { getRowComparison } from '../rowComparison';
import { ValueCell, LabelText, ValueText } from './comparisonCells';

const RowValue: React.FC<{ resolved: ResolvedRowValue }> = ({ resolved }) =>
  resolved.isHtmlRichText ? (
    <ValueText dangerouslySetInnerHTML={{ __html: resolved.value }} />
  ) : (
    <ValueText>{resolved.value}</ValueText>
  );

const KeyValueTableRenderer: React.FC<VersionLogKeyValueTableRecord> = ({ rows }) => {
  const { left, right, diff, side } = useComparisonContext();

  const renderCell = (row: VersionLogRow) => {
    const resolvedLeft = resolveRow(row, left.data, left.metadata, left.dataSources, left.properties);
    const resolvedRight = resolveRow(row, right.data, right.metadata, right.dataSources, right.properties);
    const resolved = side === 'left' ? resolvedLeft : resolvedRight;
    // A row this version cannot resolve at all has nothing to draw, whatever the diff says.
    if (!resolved) {
      return <ValueCell $variant="empty" />;
    }

    const { variant, presentation } = getCellPresentation(getRowComparison(resolvedLeft, resolvedRight, diff), side);
    switch (presentation) {
      case 'placeholder':
        return <ValueCell $variant={variant} />;
      case 'labelOnly':
        return (
          <ValueCell $variant={variant}>
            <LabelText>{row.label}</LabelText>
            <ValueText />
          </ValueCell>
        );
      default:
        return (
          <ValueCell $variant={variant}>
            <LabelText>{row.label}</LabelText>
            <RowValue resolved={resolved} />
          </ValueCell>
        );
    }
  };

  return (
    <>
      {rows.map((row) => (
        <React.Fragment key={row.propertyId}>{renderCell(row)}</React.Fragment>
      ))}
    </>
  );
};

export default KeyValueTableRenderer;
